import type { AppointmentDto, ResourceDto, UpsertResourceInput } from '@telestore/contracts';
import type { TranslationKey, TranslationParams } from '@telestore/i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { CalendarOff, ChevronLeft, ChevronRight, Plus, Trash2, UserRound, X } from 'lucide-react';
import { api, ApiError } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import {
  Button,
  Empty,
  ErrorBox,
  Field,
  Input,
  Panel,
  Pill,
  Row,
  Rows,
  Spinner,
} from '../components/ui.js';

/**
 * Services calendar (§4).
 *
 * A day at a time, not a month. A salon owner opens this to answer "who is in
 * the chair at 3pm" — a month grid answers a question nobody asks, and on a
 * phone it answers it badly.
 *
 * §6.5: Cambodia is one timezone with no DST, so every conversion here is a
 * fixed +7 rather than a reason to ship a timezone library.
 */

const PP_OFFSET_MS = 7 * 3_600_000;

/** `YYYY-MM-DD` in Phnom Penh terms. */
function phnomPenhDay(date: Date): string {
  return new Date(date.getTime() + PP_OFFSET_MS).toISOString().slice(0, 10);
}

/** The UTC instant of midnight Phnom Penh on a `YYYY-MM-DD`. */
function dayStart(day: string): Date {
  return new Date(new Date(`${day}T00:00:00Z`).getTime() - PP_OFFSET_MS);
}

function clock(iso: string): string {
  return new Date(new Date(iso).getTime() + PP_OFFSET_MS).toISOString().slice(11, 16);
}

export function CalendarPage() {
  const { shopId, t } = useSession();
  const queryClient = useQueryClient();
  const [day, setDay] = useState(() => phnomPenhDay(new Date()));
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [blocking, setBlocking] = useState(false);
  const [editing, setEditing] = useState<ResourceDto | 'new' | null>(null);

  const resources = useQuery({
    queryKey: ['resources', shopId],
    enabled: Boolean(shopId),
    queryFn: () => api.resources(shopId!),
  });

  const from = useMemo(() => dayStart(day), [day]);
  const to = useMemo(() => new Date(from.getTime() + 86_400_000), [from]);

  const appointments = useQuery({
    queryKey: ['appointments', shopId, day, resourceId],
    enabled: Boolean(shopId),
    queryFn: () =>
      api.appointments(shopId!, {
        from: from.toISOString(),
        to: to.toISOString(),
        ...(resourceId ? { resourceId } : {}),
      }),
  });

  const removeBlock = useMutation({
    mutationFn: (holdId: string) => api.removeBlock(shopId!, holdId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['appointments', shopId] });
    },
  });

  if (!shopId) return <Spinner />;

  const rows = appointments.data?.appointments ?? [];
  const isToday = day === phnomPenhDay(new Date());

  function shiftDay(days: number) {
    setDay(phnomPenhDay(new Date(dayStart(day).getTime() + days * 86_400_000 + PP_OFFSET_MS)));
  }

  return (
    <div className="flex flex-col gap-5 p-4">
      <Panel
        title={t('resources.calendar')}
        action={
          <Button variant="outline" onClick={() => setBlocking(true)}>
            <CalendarOff size={16} />
            {t('resources.blockTime')}
          </Button>
        }
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={() => shiftDay(-1)}>
            <ChevronLeft size={16} />
          </Button>
          <div className="text-center">
            <p className="text-sm font-semibold tabular-nums">{day}</p>
            {!isToday ? (
              <button
                type="button"
                className="text-xs underline"
                style={{ color: 'var(--color-ink-soft)' }}
                onClick={() => setDay(phnomPenhDay(new Date()))}
              >
                {t('common.today')}
              </button>
            ) : null}
          </div>
          <Button variant="ghost" onClick={() => shiftDay(1)}>
            <ChevronRight size={16} />
          </Button>
        </div>

        {/* Filter by person, but only when there is more than one. */}
        {(resources.data?.resources.length ?? 0) > 1 ? (
          <div className="mb-3 flex flex-wrap gap-2">
            <Chip
              label={t('storefront.anyStaff')}
              active={resourceId === null}
              onClick={() => setResourceId(null)}
            />
            {resources.data?.resources.map((resource) => (
              <Chip
                key={resource.id}
                label={resource.name}
                active={resourceId === resource.id}
                onClick={() => setResourceId(resource.id)}
              />
            ))}
          </div>
        ) : null}

        {appointments.isPending ? <Spinner /> : null}
        {!appointments.isPending && rows.length === 0 ? (
          <Empty title={t('resources.noAppointments')} />
        ) : null}

        <Rows>
          {rows.map((entry) => (
            <AppointmentRow
              key={entry.id}
              entry={entry}
              t={t}
              onRemoveBlock={() => removeBlock.mutate(entry.id)}
            />
          ))}
        </Rows>
      </Panel>

      <Panel
        title={t('resources.title')}
        action={
          <Button onClick={() => setEditing('new')}>
            <Plus size={16} />
            {t('resources.newResource')}
          </Button>
        }
      >
        <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
          {/* §4: staff in `identity-team` ARE the bookable resources. */}
          {t('resources.staffHint')}
        </p>

        {resources.isPending ? <Spinner /> : null}
        {!resources.isPending && (resources.data?.resources.length ?? 0) === 0 ? (
          <Empty title={t('common.empty')} />
        ) : null}

        <Rows>
          {resources.data?.resources.map((resource) => (
            <Row key={resource.id} onClick={() => setEditing(resource)}>
              <span className="flex items-center gap-2">
                <UserRound size={14} className="opacity-40" aria-hidden="true" />
                <span className="font-medium">{resource.name}</span>
                {!resource.isActive ? <Pill tone="warn">{t('resources.inactive')}</Pill> : null}
              </span>
              <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                {resource.hours.length > 0
                  ? t('resources.daysPerWeek', {
                      count: new Set(resource.hours.map((h) => h.weekday)).size,
                    })
                  : t('resources.noHours')}
              </span>
            </Row>
          ))}
        </Rows>
      </Panel>

      {editing ? (
        <ResourceEditor
          resource={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['resources', shopId] });
          }}
        />
      ) : null}

      {blocking ? (
        <BlockEditor
          resources={resources.data?.resources ?? []}
          day={day}
          onClose={() => setBlocking(false)}
          onSaved={() => {
            setBlocking(false);
            void queryClient.invalidateQueries({ queryKey: ['appointments', shopId] });
          }}
        />
      ) : null}
    </div>
  );
}

function AppointmentRow({
  entry,
  t,
  onRemoveBlock,
}: {
  entry: AppointmentDto;
  t: (key: TranslationKey, params?: TranslationParams) => string;
  onRemoveBlock: () => void;
}) {
  /**
   * A block is not a booking: it is the merchant's own lunch or day off, and
   * the only thing to do with it is take it away again.
   */
  const isBlock = entry.kind === 'block';

  return (
    <Row>
      <span className="flex items-center gap-3">
        <span className="tabular-nums font-semibold">{clock(entry.startsAt)}</span>
        <span className="flex flex-col">
          <span className={isBlock ? 'italic opacity-70' : 'font-medium'}>
            {isBlock ? t('resources.blocked') : entry.serviceName ?? ''}
          </span>
          <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
            {[entry.customerName, entry.resourceName].filter(Boolean).join(' · ')}
          </span>
          {entry.note ? (
            <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
              {entry.note}
            </span>
          ) : null}
        </span>
      </span>

      {isBlock ? (
        <Button variant="ghost" onClick={onRemoveBlock}>
          <Trash2 size={16} />
        </Button>
      ) : (
        <Pill tone={entry.status === 'no_show' ? 'warn' : 'neutral'}>
          {entry.orderCode ?? ''}
        </Pill>
      )}
    </Row>
  );
}

const WEEKDAYS = [0, 1, 2, 3, 4, 5, 6] as const;
const WEEKDAY_KEYS = [
  'common.sun',
  'common.mon',
  'common.tue',
  'common.wed',
  'common.thu',
  'common.fri',
  'common.sat',
] as const;

function ResourceEditor({
  resource,
  onClose,
  onSaved,
}: {
  resource: ResourceDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { shopId, t } = useSession();
  const [name, setName] = useState(resource?.name ?? '');
  const [error, setError] = useState<string | null>(null);

  /**
   * One open/close pair per weekday, which covers every salon and clinic we
   * have seen. A split shift (morning, siesta, evening) is expressible in the
   * schema — `workingHoursSchema` is a list — but the editor keeps to one row
   * per day until a merchant actually asks for two.
   */
  const [hours, setHours] = useState<Record<number, { open: string; close: string } | null>>(() => {
    const initial: Record<number, { open: string; close: string } | null> = {};
    for (const weekday of WEEKDAYS) {
      const found = resource?.hours.find((entry) => entry.weekday === weekday);
      initial[weekday] = found ? { open: found.open, close: found.close } : null;
    }
    return initial;
  });

  const save = useMutation({
    mutationFn: () => {
      const body: UpsertResourceInput = {
        name: name.trim(),
        userId: resource?.userId ?? null,
        photoUrl: resource?.photoUrl ?? null,
        isActive: resource?.isActive ?? true,
        position: resource?.position ?? 0,
        hours: WEEKDAYS.flatMap((weekday) => {
          const entry = hours[weekday];
          return entry ? [{ weekday, open: entry.open, close: entry.close }] : [];
        }),
      };
      return resource
        ? api.updateResource(shopId!, resource.id, body)
        : api.createResource(shopId!, body);
    },
    onSuccess: onSaved,
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  const deactivate = useMutation({
    mutationFn: () => api.deactivateResource(shopId!, resource!.id),
    onSuccess: onSaved,
  });

  return (
    <Sheet title={resource ? t('common.edit') : t('resources.newResource')} onClose={onClose}>
      <Field label={t('checkout.name')}>
        <Input value={name} onChange={(event) => setName(event.target.value)} />
      </Field>

      <div className="mt-3">
        <p className="mb-2 text-sm font-medium">{t('resources.workingHours')}</p>
        <div className="flex flex-col gap-2">
          {WEEKDAYS.map((weekday) => {
            const entry = hours[weekday];
            return (
              <div key={weekday} className="flex items-center gap-2">
                <button
                  type="button"
                  className="w-12 shrink-0 rounded-md py-1.5 text-xs font-medium"
                  style={{
                    border: `1px solid ${entry ? 'var(--color-accent)' : 'var(--color-line)'}`,
                    background: entry ? 'var(--color-accent)' : 'transparent',
                    color: entry ? 'var(--color-accent-contrast)' : 'var(--color-ink-soft)',
                  }}
                  onClick={() =>
                    setHours((current) => ({
                      ...current,
                      // A sensible default beats an empty pair of time fields.
                      [weekday]: entry ? null : { open: '09:00', close: '18:00' },
                    }))
                  }
                >
                  {t(WEEKDAY_KEYS[weekday])}
                </button>

                {entry ? (
                  <>
                    <Input
                      type="time"
                      value={entry.open}
                      onChange={(event) =>
                        setHours((current) => ({
                          ...current,
                          [weekday]: { ...entry, open: event.target.value },
                        }))
                      }
                    />
                    <Input
                      type="time"
                      value={entry.close}
                      onChange={(event) =>
                        setHours((current) => ({
                          ...current,
                          [weekday]: { ...entry, close: event.target.value },
                        }))
                      }
                    />
                  </>
                ) : (
                  <span className="text-xs" style={{ color: 'var(--color-ink-soft)' }}>
                    {t('resources.dayOff')}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {error ? <div className="mt-3"><ErrorBox message={error} /></div> : null}

      <Button
        className="mt-4 w-full"
        disabled={save.isPending || name.trim().length < 1}
        onClick={() => save.mutate()}
      >
        {save.isPending ? t('common.loading') : t('common.save')}
      </Button>

      {resource && resource.isActive ? (
        <Button
          variant="outline"
          className="mt-2 w-full"
          disabled={deactivate.isPending}
          onClick={() => deactivate.mutate()}
        >
          {t('resources.deactivate')}
        </Button>
      ) : null}
    </Sheet>
  );
}

function BlockEditor({
  resources,
  day,
  onClose,
  onSaved,
}: {
  resources: ResourceDto[];
  day: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { shopId, t } = useSession();
  const [resourceId, setResourceId] = useState(resources[0]?.id ?? '');
  const [start, setStart] = useState('12:00');
  const [end, setEnd] = useState('13:00');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      api.blockTime(shopId!, {
        resourceId,
        // Local wall clock on the chosen day, converted once at the boundary.
        startsAt: new Date(dayStart(day).getTime() + minutesOf(start) * 60_000).toISOString(),
        endsAt: new Date(dayStart(day).getTime() + minutesOf(end) * 60_000).toISOString(),
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
    onSuccess: onSaved,
    onError: (cause) => setError(cause instanceof ApiError ? cause.message : t('errors.internal')),
  });

  return (
    <Sheet title={t('resources.blockTime')} onClose={onClose}>
      <p className="mb-3 text-sm" style={{ color: 'var(--color-ink-soft)' }}>
        {t('resources.blockHint')}
      </p>

      {resources.length > 1 ? (
        <div className="mb-3 flex flex-wrap gap-2">
          {resources.map((resource) => (
            <Chip
              key={resource.id}
              label={resource.name}
              active={resourceId === resource.id}
              onClick={() => setResourceId(resource.id)}
            />
          ))}
        </div>
      ) : null}

      <div className="flex gap-2">
        <Field label={t('common.from')}>
          <Input type="time" value={start} onChange={(event) => setStart(event.target.value)} />
        </Field>
        <Field label={t('common.until')}>
          <Input type="time" value={end} onChange={(event) => setEnd(event.target.value)} />
        </Field>
      </div>

      <div className="mt-3">
        <Field label={`${t('checkout.note')} (${t('common.optional')})`}>
          <Input value={note} onChange={(event) => setNote(event.target.value)} />
        </Field>
      </div>

      {error ? <div className="mt-3"><ErrorBox message={error} /></div> : null}

      <Button
        className="mt-4 w-full"
        disabled={save.isPending || !resourceId || minutesOf(end) <= minutesOf(start)}
        onClick={() => save.mutate()}
      >
        {save.isPending ? t('common.loading') : t('common.save')}
      </Button>
    </Sheet>
  );
}

function minutesOf(value: string): number {
  const [hours, minutes] = value.split(':').map(Number);
  return (hours ?? 0) * 60 + (minutes ?? 0);
}

function Sheet({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      className="fixed inset-0 z-30 flex items-end justify-center sm:items-center"
      style={{ background: 'rgb(0 0 0 / 0.4)' }}
      onClick={onClose}
    >
      <div
        className="max-h-[90dvh] w-full max-w-lg overflow-y-auto rounded-t-2xl p-4 sm:rounded-2xl"
        style={{ background: 'var(--color-panel)' }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold">{title}</h2>
          <Button variant="ghost" onClick={onClose}>
            <X size={16} />
          </Button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Chip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-full px-3 py-1.5 text-xs font-medium"
      style={{
        border: `1px solid ${active ? 'var(--color-accent)' : 'var(--color-line)'}`,
        background: active ? 'var(--color-accent)' : 'transparent',
        color: active ? 'var(--color-accent-contrast)' : 'var(--color-ink)',
      }}
    >
      {label}
    </button>
  );
}
